/**
 * Client shell: login/register → character select → game canvas + HUD.
 * The session token persists across reloads via the client transport edge
 * (net/platform.ts) so a refresh lands back in the world (the resume path
 * exercises networking.md semantics).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { subscribeBarks } from './game/audio/index.js';

import { BODY_TYPES, DEFAULT_APPEARANCE, GAME_NAME, TRADE_MARK } from '@great-cloud/shared/client';
import type {
  AppearanceOptionDef,
  AssetPackDef,
  CharacterAppearance,
  ClientPackDef,
  PresentationRef,
  ThemeDef,
} from '@great-cloud/shared/client';

import {
  ApiError,
  acceptPrestigeOfferByToken,
  unsubscribeOfferMail,
  appealLogin,
  changePassword,
  completeEmailVerification,
  completePasswordReset,
  isVerifyRequired,
  logout as logoutServer,
  requestEmailVerification,
  requestPasswordReset,
  createCharacter,
  fetchAppearanceOptions,
  fileAppeal,
  listAppealActions,
  listCharacters,
  login,
  isMfaChallenge,
  mfaConfirm,
  mfaDisable,
  mfaEmailConfirm,
  mfaEmailDisable,
  mfaEmailRequest,
  mfaSetup,
  mfaStatus,
  mfaVerify,
  register,
} from './net/api.js';
import {
  MAIL_ACCEPT_BUTTON,
  MAIL_ACCEPT_INTRO,
  MAIL_LINK_INVALID,
  MAIL_UNSUBSCRIBE_BUTTON,
  MAIL_UNSUBSCRIBE_INTRO,
  MAIL_UNSUBSCRIBED,
  acceptedWords,
  parsePrestigeMailLink,
  refusedWords,
  type PrestigeMailLink,
} from './ui/prestigeMailLink.js';
import type {
  AppealActionSummary,
  AppearanceOptionsResponse,
  CharacterSummary,
  MfaStatus,
} from './net/api.js';
import { channelHooks } from './net/channel.js';
import { GameConnection } from './net/connection.js';
import { binarySnapshotsWanted, requestedSnapshotHz } from './net/snapshotRate.js';
import { clearToken, getToken, setToken } from './net/platform.js';
import { ContentPackStore } from './content/pack.js';
import { setContentNames } from './ui/playerText.js';
import { setWayfindingContent } from './ui/wayfinding.js';
import { Game } from './game/Game.js';
import type { GameStatus } from './game/Game.js';
import { devToolsEnabled, effectiveSearch, terrainReliefOn } from './game/devFlags.js';
import { parseCameraMove } from './game/cameraMove.js';
import { parseDevDungeonOptions } from './game/dungeon/previewSpot.js';
import { devBridge } from './game/devBridge.js';
import {
  createGameRenderer,
  disposeCreatedRenderer,
  type CreatedRenderer,
  type RendererLossInfo,
} from './game/renderer.js';
import type { SceneContentLookup } from './game/scene/index.js';
import type {
  ClientCosmetic,
  EquipmentBridge,
  EquipmentContent,
  EquippedItemsSource,
  GatherToolBridge,
} from './game/equipment/index.js';
import { equippedItemsForCharacter } from './game/equipment/index.js';
import type { CreatureBridge, CreatureContent, EnemyPresentation } from './game/creatures/index.js';
import type { RigBridge, RigContent } from './game/rig-avatar/index.js';
import type { NpcBridge, NpcContent, NpcPresentation } from './game/npcs/index.js';
import type { NodeBridge, NodeContent, ResourcePresentation } from './game/nodes/index.js';
import { CaptureController } from './ui/CaptureController.js';
import { NetProvider } from './ui/net-context.js';
import { RendererStartFailedNotice } from './ui/RendererStartFailedNotice.js';
import type { CraftRecipeView } from './ui/panels/craftable.js';
import { restoreZoneRoute } from './ui/panels/playtestJump.js';
import { PrestigeOfferPrompt } from './ui/PrestigeOfferPrompt.js';
import { routedDevZones } from './net/devZones.js';
import { ClientStore } from './state/store.js';
import {
  READY_ATTRIBUTE,
  ReadinessSession,
  publishReadiness,
  type ReadinessGlobals,
} from './game/readiness.js';
import { LoadingScreen } from './ui/loading/LoadingScreen.js';
import { GuestApp } from './guest/GuestApp.js';
import type { GuestGameProps } from './guest/GuestApp.js';
import { guestBootRequested, initialAuthMode } from './guest/guestBoot.js';
import {
  COMPLETION_CARD_DELAY_MS,
  endReasonForFailure,
  finalQuestTurnedIn,
  msUntilExpiry,
} from './guest/guestEnd.js';
import { WallNotifier } from './guest/guestWalls.js';
import { DuplicateLoginDialog } from './ui/DuplicateLoginDialog.js';
import { kickedRoute } from './ui/duplicateLoginCopy.js';

type Screen =
  | { kind: 'auth'; notice?: string }
  | { kind: 'select'; token: string; characters: CharacterSummary[] }
  | { kind: 'game'; token: string; characterId: number };

function AuthScreen({
  onAuthed,
  initialNotice = '',
}: {
  onAuthed: (token: string, chars: CharacterSummary[]) => void;
  /**
   * A one-line notice shown on arrival: AD-794 (`?guest=1` on an origin with no demo), or
   * q-duplicate-login-warning ("logged in from another device").
   */
  initialNotice?: string;
}) {
  // AD-794 §6.4: `?from=demo` (the end card's "Create your account") opens REGISTER directly.
  const [mode, setMode] = useState<
    'login' | 'register' | 'appeal' | 'forgot' | 'reset' | 'verify' | 'mfa' | 'prestige-mail'
  >(() => initialAuthMode(window.location.search, window.location.hash));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [characterName, setCharacterName] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(initialNotice);
  const [busy, setBusy] = useState(false);
  // Password reset via email (AD-314 §3.8). The mail's link is fragment-routed
  // (`#/reset=<token>`, T-1: the token never reaches a server/proxy log); on
  // first render we lift the token out of the URL, strip it from the address
  // bar, and open the set-a-new-password form. The token identifies the
  // account — no email field is needed on that form.
  const [resetToken, setResetToken] = useState<string | null>(null);
  useEffect(() => {
    const m = /^#\/reset=([0-9a-f]{16,200})$/.exec(window.location.hash);
    if (!m) return;
    setResetToken(m[1] ?? null);
    setMode('reset');
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }, []);
  // Signup email verification (AD-332 item 1; design §7a). The mail's link is
  // fragment-routed like the reset link (`#/verify=<token>`, T-1); on first
  // render we lift the token and strip it from the address bar, then wait for
  // the player to press "Confirm my email" — opening the link must not spend
  // the single-use token, because mailbox link scanners that run page script
  // (Safe Links-style detonation) open it before the player does (AD-9002).
  // Completing does NOT log the player in (they sign in normally).
  // `unverifiedEmail` remembers a login refused `emailUnverified` so the screen
  // can offer a one-click resend.
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);
  const [verifyToken, setVerifyToken] = useState<string | null>(null);
  useEffect(() => {
    const m = /^#\/verify=([0-9a-f]{16,200})$/.exec(window.location.hash);
    if (!m || !m[1]) return;
    setVerifyToken(m[1]);
    setMode('verify');
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }, []);

  // The prestige offer mail's links (AD-779 S5; ui/prestigeMailLink.ts): lifted and stripped
  // like the verify link, and spent only on the player's press (link scanners open it first).
  const [prestigeLink, setPrestigeLink] = useState<PrestigeMailLink | null>(null);
  useEffect(() => {
    const link = parsePrestigeMailLink(window.location.hash);
    if (!link) return;
    setPrestigeLink(link);
    setMode('prestige-mail');
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  }, []);

  const answerPrestigeLink = async (): Promise<void> => {
    if (prestigeLink === null) return;
    setBusy(true);
    setError('');
    try {
      if (prestigeLink.kind === 'accept') {
        const r = await acceptPrestigeOfferByToken(prestigeLink.token);
        setNotice(acceptedWords(r.held));
      } else {
        await unsubscribeOfferMail(prestigeLink.token);
        setNotice(MAIL_UNSUBSCRIBED);
      }
    } catch (e) {
      // A rate limit or a network fault spent nothing: keep the link on screen to retry
      // (T3 mini-check P3). Any other answer is final.
      if (e instanceof ApiError && e.code === 'rateLimited') {
        setError('Too many attempts — please wait a minute and try again.');
        setBusy(false);
        return;
      }
      if (!(e instanceof ApiError)) {
        setError('network error — is the server running?');
        setBusy(false);
        return;
      }
      setError(prestigeLink.kind === 'accept' ? refusedWords(e.code) : MAIL_LINK_INVALID);
    }
    setPrestigeLink(null);
    setMode('login');
    setBusy(false);
  };

  const confirmEmail = async (): Promise<void> => {
    if (verifyToken === null) return;
    setBusy(true);
    setError('');
    try {
      await completeEmailVerification(verifyToken);
      setNotice('Email confirmed — you can log in now.');
    } catch (e) {
      if (e instanceof ApiError && e.code === 'verifyInvalid') {
        setError('This link has expired or was already used — log in to request a new one.');
      } else {
        setError(e instanceof ApiError ? e.message : 'network error — is the server running?');
      }
    } finally {
      setVerifyToken(null);
      setMode('login');
      setBusy(false);
    }
  };

  const resendVerification = async (): Promise<void> => {
    const address = unverifiedEmail ?? email;
    if (address.length === 0) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await requestEmailVerification(address);
      // The same copy for every address — this route is not an oracle either.
      setNotice('If that address needs confirming, a new link is on its way. Check your inbox.');
    } catch (e) {
      if (e instanceof ApiError && e.code === 'verifyUnavailable') {
        setError('Email verification is not available yet — please contact support.');
      } else if (e instanceof ApiError && e.code === 'rateLimited') {
        setError('Too many attempts — please wait a minute and try again.');
      } else {
        setError(e instanceof ApiError ? e.message : 'network error — is the server running?');
      }
    } finally {
      setBusy(false);
    }
  };

  const forgot = async (): Promise<void> => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await requestPasswordReset(email);
      // The same copy for every address (T-2) — this screen is not an oracle either.
      setNotice(
        'If an account exists for that address, a reset link is on its way. It is valid for 30 minutes.',
      );
    } catch (e) {
      if (e instanceof ApiError && e.code === 'resetUnavailable') {
        setError('Password reset is not available yet — please contact support.');
      } else if (e instanceof ApiError && e.code === 'rateLimited') {
        setError('Too many attempts — please wait a minute and try again.');
      } else {
        setError(e instanceof ApiError ? e.message : 'network error — is the server running?');
      }
    } finally {
      setBusy(false);
    }
  };

  const reset = async (): Promise<void> => {
    if (resetToken === null) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await completePasswordReset(resetToken, password);
      setResetToken(null);
      setPassword('');
      setMode('login');
      setNotice('Password updated — please sign in.');
    } catch (e) {
      if (e instanceof ApiError && e.code === 'resetInvalid') {
        setError('This link has expired or was already used — request a new one.');
      } else if (e instanceof ApiError && e.code === 'badPassword') {
        setError('Choose a password of 8 to 128 characters.');
      } else if (e instanceof ApiError && e.code === 'rateLimited') {
        setError('Too many attempts — please wait a minute and try again.');
      } else {
        setError(e instanceof ApiError ? e.message : 'network error — is the server running?');
      }
    } finally {
      setBusy(false);
    }
  };
  // The banned-player appeal channel (SEC-6 §6.2 item 2): a 403 accountRestricted
  // at login is the one moment a banned player is provably in front of this
  // screen, so the appeal affordance lives HERE — same origin, no second delivery
  // mechanism. It uses the SAME credentials against /api/appeal/login, which
  // skips the moderation login gate and issues a token that reaches nothing but
  // the appeal routes.
  const [restricted, setRestricted] = useState(false);
  // The MFA step (AD-314 §4.1): when the account is enrolled, login answers a
  // five-minute challenge token instead of a session. It lives in THIS
  // component's state only (never setToken) and dies with the screen; the code
  // — or a recovery code — is verified by /api/auth/mfa/verify, which issues the
  // real session.
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  // 'email' = the AD-332 email-OTP tier: the code was MAILED (design §7b item 3).
  const [mfaMethod, setMfaMethod] = useState<'totp' | 'email'>('totp');

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      if (mode === 'login') {
        const res = await login(email, password);
        if (isMfaChallenge(res)) {
          setMfaToken(res.mfaToken);
          setMfaCode('');
          setUseRecovery(false);
          setMfaMethod(res.method ?? 'totp');
          setMode('mfa');
          return;
        }
        setToken(res.token);
        onAuthed(res.token, res.characters);
        return;
      }
      const res = await register(email, password, characterName, accessCode);
      if (isVerifyRequired(res)) {
        // AD-332 item 1: the account exists but is not playable until the
        // emailed link is opened. Land on the login form with the copy.
        setMode('login');
        setUnverifiedEmail(res.account.email);
        setNotice(
          'Account created — check your email and open the confirmation link, then log in.',
        );
        return;
      }
      setToken(res.token);
      onAuthed(res.token, res.characters);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'network error — is the server running?');
      setRestricted(e instanceof ApiError && e.code === 'accountRestricted');
      if (e instanceof ApiError && e.code === 'emailUnverified') {
        setUnverifiedEmail(email);
        setError('Confirm your email address to continue — check your inbox for the link.');
      }
    } finally {
      setBusy(false);
    }
  };

  const verifyMfa = async (): Promise<void> => {
    if (mfaToken === null) return;
    setBusy(true);
    setError('');
    try {
      const res = await mfaVerify(
        mfaToken,
        useRecovery ? { recoveryCode: mfaCode } : { code: mfaCode.replace(/\s+/g, '') },
      );
      setMfaToken(null);
      setMfaCode('');
      setToken(res.token);
      onAuthed(res.token, res.characters);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'mfaFailed') {
        setError(useRecovery ? 'That recovery code is not valid.' : 'That code is not valid.');
      } else if (e instanceof ApiError && e.code === 'mfaChallengeInvalid') {
        setMfaToken(null);
        setMode('login');
        setError('That sign-in expired — please log in again.');
      } else if (e instanceof ApiError && e.code === 'rateLimited') {
        setError('Too many attempts — please wait a minute and try again.');
      } else {
        setError(e instanceof ApiError ? e.message : 'network error — is the server running?');
      }
    } finally {
      setBusy(false);
    }
  };

  if (mode === 'appeal') {
    return (
      <AppealScreen
        email={email}
        password={password}
        onBack={() => {
          setMode('login');
          setError('');
        }}
      />
    );
  }

  if (mode === 'mfa') {
    return (
      <div className="screen">
        <div className="panel">
          <h1>
            {GAME_NAME}
            <sup className="tgc-tm">{TRADE_MARK}</sup>
          </h1>
          <div className="sub">Two-factor authentication</div>
          <div className="sub">
            {useRecovery
              ? 'Enter one of your recovery codes.'
              : mfaMethod === 'email'
                ? 'Enter the 8-digit code we just emailed you.'
                : 'Enter the 6-digit code from your authenticator app.'}
          </div>
          <input
            placeholder={
              useRecovery
                ? 'recovery code (XXXXX-XXXXX)'
                : mfaMethod === 'email'
                  ? '8-digit code'
                  : '6-digit code'
            }
            value={mfaCode}
            inputMode={useRecovery ? 'text' : 'numeric'}
            autoComplete="one-time-code"
            onChange={(e) => setMfaCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void verifyMfa();
            }}
          />
          <div className="error">{error}</div>
          <button disabled={busy || mfaCode.trim().length === 0} onClick={() => void verifyMfa()}>
            Verify
          </button>
          {mfaMethod === 'totp' && (
            <button
              className="secondary"
              disabled={busy}
              onClick={() => {
                setUseRecovery(!useRecovery);
                setMfaCode('');
                setError('');
              }}
            >
              {useRecovery ? 'Use the authenticator app instead' : 'Use a recovery code instead'}
            </button>
          )}
          <button
            className="secondary"
            disabled={busy}
            onClick={() => {
              setMfaToken(null);
              setMode('login');
              setError('');
            }}
          >
            Back to log in
          </button>
        </div>
      </div>
    );
  }

  if (mode === 'forgot') {
    return (
      <div className="screen">
        <div className="panel">
          <h1>
            {GAME_NAME}
            <sup className="tgc-tm">{TRADE_MARK}</sup>
          </h1>
          <div className="sub">Forgot your password?</div>
          <input
            placeholder="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
          />
          <div className="error">{error}</div>
          {notice ? <div className="sub">{notice}</div> : null}
          <button disabled={busy || email.length === 0} onClick={() => void forgot()}>
            Send reset link
          </button>
          <button
            className="secondary"
            disabled={busy}
            onClick={() => {
              setMode('login');
              setError('');
              setNotice('');
            }}
          >
            Back to log in
          </button>
        </div>
      </div>
    );
  }

  if (mode === 'prestige-mail') {
    const accept = prestigeLink?.kind !== 'unsubscribe';
    return (
      <div className="screen">
        <div className="panel">
          <h1>
            {GAME_NAME}
            <sup className="tgc-tm">{TRADE_MARK}</sup>
          </h1>
          <div className="sub">{accept ? MAIL_ACCEPT_INTRO : MAIL_UNSUBSCRIBE_INTRO}</div>
          <div className="error">{error}</div>
          <button
            disabled={busy || prestigeLink === null}
            onClick={() => void answerPrestigeLink()}
          >
            {accept ? MAIL_ACCEPT_BUTTON : MAIL_UNSUBSCRIBE_BUTTON}
          </button>
        </div>
      </div>
    );
  }

  if (mode === 'verify') {
    return (
      <div className="screen">
        <div className="panel">
          <h1>
            {GAME_NAME}
            <sup className="tgc-tm">{TRADE_MARK}</sup>
          </h1>
          <div className="sub">Confirm your email address to finish creating your account.</div>
          <button disabled={busy || verifyToken === null} onClick={() => void confirmEmail()}>
            Confirm my email
          </button>
        </div>
      </div>
    );
  }

  if (mode === 'reset') {
    return (
      <div className="screen">
        <div className="panel">
          <h1>
            {GAME_NAME}
            <sup className="tgc-tm">{TRADE_MARK}</sup>
          </h1>
          <div className="sub">Set a new password</div>
          <input
            placeholder="new password (8+ characters)"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
          />
          <div className="error">{error}</div>
          <button disabled={busy || resetToken === null} onClick={() => void reset()}>
            Set new password
          </button>
          <button
            className="secondary"
            disabled={busy}
            onClick={() => {
              setMode('forgot');
              setError('');
              setNotice('');
            }}
          >
            Request a new link
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      <div className="panel">
        <h1>
          {GAME_NAME}
          <sup className="tgc-tm">{TRADE_MARK}</sup>
        </h1>
        <div className="sub">core multiplayer prototype — Phase 3</div>
        <input
          placeholder="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
        />
        <input
          placeholder="password (8+ characters)"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
        />
        {mode === 'register' ? (
          <input
            placeholder="character name"
            value={characterName}
            onChange={(e) => setCharacterName(e.target.value)}
          />
        ) : null}
        {mode === 'register' ? (
          <input
            placeholder="access code"
            value={accessCode}
            onChange={(e) => setAccessCode(e.target.value)}
            autoComplete="off"
          />
        ) : null}
        <div className="error">{error}</div>
        {notice ? (
          <div className="sub" role="status">
            {notice}
          </div>
        ) : null}
        <button disabled={busy} onClick={() => void submit()}>
          {mode === 'login' ? 'Log in' : 'Create account'}
        </button>
        {mode === 'login' ? (
          <button
            className="secondary"
            disabled={busy}
            onClick={() => {
              setMode('forgot');
              setError('');
              setNotice('');
            }}
          >
            Forgot password?
          </button>
        ) : null}
        {restricted && mode === 'login' ? (
          <button className="secondary" disabled={busy} onClick={() => setMode('appeal')}>
            Appeal this restriction
          </button>
        ) : null}
        {unverifiedEmail !== null && mode === 'login' ? (
          <button className="secondary" disabled={busy} onClick={() => void resendVerification()}>
            Resend confirmation email
          </button>
        ) : null}
        <button
          className="secondary"
          disabled={busy}
          onClick={() => {
            setMode(mode === 'login' ? 'register' : 'login');
            setError('');
          }}
        >
          {mode === 'login' ? 'New here? Create an account' : 'Have an account? Log in'}
        </button>
      </div>
    </div>
  );
}

/**
 * The banned-player appeal channel's client (SEC-6 §6.2 item 2). Logs in through
 * /api/appeal/login with the credentials the player just typed, lists the
 * account's OWN enforcement record, and files one appeal per action. The appeal
 * token is held in component state only — it is a fixed-lifetime credential that
 * reaches nothing else, so it is never stored via setToken().
 */
function AppealScreen({
  email,
  password,
  onBack,
}: {
  email: string;
  password: string;
  onBack: () => void;
}) {
  const [appealToken, setAppealToken] = useState<string | null>(null);
  const [actions, setActions] = useState<AppealActionSummary[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [statement, setStatement] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async (token: string): Promise<void> => {
    const res = await listAppealActions(token);
    setActions(res.actions);
    const first = res.actions.find((a) => a.appealable && a.appeal === null);
    setSelected(first ? first.id : null);
  };

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    appealLogin(email, password)
      .then(async (session) => {
        if (cancelled) return;
        setAppealToken(session.token);
        await load(session.token);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : 'network error');
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [email, password]);

  const submit = async (): Promise<void> => {
    if (appealToken === null || selected === null) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await fileAppeal(appealToken, selected, statement);
      setNotice('Your appeal was filed. A moderator other than the one who acted will review it.');
      setStatement('');
      await load(appealToken);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'network error');
    } finally {
      setBusy(false);
    }
  };

  const describe = (a: AppealActionSummary): string => {
    const when = new Date(a.createdAt).toLocaleString();
    const until = a.expiresAt ? ` until ${new Date(a.expiresAt).toLocaleString()}` : '';
    const lifted = a.liftedAt ? ' (lifted)' : '';
    const why = a.reason ? ` — ${a.reason}` : '';
    const state = a.appeal
      ? ` · appeal ${a.appeal.status}`
      : a.appealable
        ? ''
        : ' · not appealable';
    return `${a.action}${until}${lifted}, ${when}${why}${state}`;
  };

  return (
    <div className="screen">
      <div className="panel">
        <h1>
          {GAME_NAME}
          <sup className="tgc-tm">{TRADE_MARK}</sup>
        </h1>
        <div className="sub">Appeal a restriction</div>
        {actions.length === 0 && !busy && !error ? (
          <div className="sub">No moderation actions are recorded against this account.</div>
        ) : null}
        {actions.map((a) => (
          <label key={a.id} style={{ display: 'block', textAlign: 'left' }}>
            <input
              type="radio"
              name="appeal-action"
              disabled={busy || !a.appealable || a.appeal !== null}
              checked={selected === a.id}
              onChange={() => setSelected(a.id)}
            />{' '}
            {describe(a)}
          </label>
        ))}
        <textarea
          placeholder="Why should this decision be reviewed? (up to 2000 characters)"
          value={statement}
          maxLength={2000}
          rows={5}
          disabled={busy || selected === null}
          onChange={(e) => setStatement(e.target.value)}
        />
        <div className="error">{error}</div>
        {notice ? <div className="sub">{notice}</div> : null}
        <button
          disabled={busy || selected === null || statement.trim().length === 0}
          onClick={() => void submit()}
        >
          File appeal
        </button>
        <button className="secondary" disabled={busy} onClick={onBack}>
          Back to log in
        </button>
      </div>
    </div>
  );
}

function SelectScreen({
  token,
  characters,
  onEnter,
  onLogout,
  onCreated,
}: {
  token: string;
  characters: CharacterSummary[];
  onEnter: (characterId: number) => void;
  onLogout: () => void;
  onCreated: (c: CharacterSummary) => void;
}) {
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // Appearance customisation (D-6; AD-137). Body builds are the fixed BODY_TYPES
  // enum; the face/hair/skin wardrobe is fetched from the server (data-driven).
  const [appearance, setAppearance] = useState<CharacterAppearance>({ ...DEFAULT_APPEARANCE });
  const [wardrobe, setWardrobe] = useState<AppearanceOptionsResponse | null>(null);
  useEffect(() => {
    void fetchAppearanceOptions()
      .then(setWardrobe)
      .catch(() => setWardrobe(null));
  }, []);
  // Change password (AD-314 §3.2 B): re-authenticates with the current password;
  // every OTHER session of the account is revoked, this one is kept.
  const [pwOpen, setPwOpen] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [pwNotice, setPwNotice] = useState('');
  // Two-factor authentication (AD-314 §4; `q-mfa-totp-enrolment`). Enrolment
  // is setup (re-enter password → secret shown once) → confirm (first code from
  // the app → recovery codes shown ONCE) ; disable needs password + code.
  const [mfa, setMfa] = useState<MfaStatus | null>(null);
  const [mfaOpen, setMfaOpen] = useState(false);
  const [mfaStage, setMfaStage] = useState<
    'idle' | 'setup' | 'codes' | 'disable' | 'email-confirm' | 'email-disable'
  >('idle');
  const [mfaPw, setMfaPw] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaSecret, setMfaSecret] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [mfaNotice, setMfaNotice] = useState('');
  useEffect(() => {
    void mfaStatus()
      .then(setMfa)
      .catch(() => setMfa(null));
  }, []);
  const refreshMfa = (): void => {
    void mfaStatus()
      .then(setMfa)
      .catch(() => undefined);
  };
  const mfaError = (e: unknown, fallback: string): void => {
    if (e instanceof ApiError && e.code === 'authFailed') setError('Password is incorrect.');
    else if (e instanceof ApiError && e.code === 'mfaFailed') setError('That code is not valid.');
    else if (e instanceof ApiError && e.code === 'mfaUnavailable')
      setError('Two-factor authentication is not available on this server yet.');
    else if (e instanceof ApiError && e.code === 'mfaNoPending')
      setError('That setup expired — start again.');
    else if (e instanceof ApiError && e.code === 'mfaRequiredForModerators')
      setError('Moderator accounts must keep two-factor authentication on.');
    else if (e instanceof ApiError && e.code === 'mfaAlreadyEnrolled')
      setError('Two-factor authentication is already on for this account.');
    else setError(e instanceof ApiError ? e.message : fallback);
  };
  const startMfa = async (): Promise<void> => {
    setBusy(true);
    setError('');
    setMfaNotice('');
    try {
      const res = await mfaSetup(mfaPw);
      setMfaPw('');
      setMfaSecret({ secret: res.secret, otpauthUri: res.otpauthUri });
      setMfaCode('');
      setMfaStage('setup');
    } catch (e) {
      mfaError(e, 'network error — is the server running?');
    } finally {
      setBusy(false);
    }
  };
  const confirmMfa = async (): Promise<void> => {
    setBusy(true);
    setError('');
    try {
      const res = await mfaConfirm(mfaCode.replace(/\s+/g, ''));
      setMfaSecret(null);
      setMfaCode('');
      setRecoveryCodes(res.recoveryCodes);
      setMfaStage('codes');
      setMfaNotice('Two-factor authentication is on. Your other devices have been signed out.');
      refreshMfa();
    } catch (e) {
      mfaError(e, 'network error — is the server running?');
    } finally {
      setBusy(false);
    }
  };
  const disableMfa = async (): Promise<void> => {
    setBusy(true);
    setError('');
    setMfaNotice('');
    try {
      const typed = mfaCode.trim();
      await mfaDisable(
        mfaPw,
        /^\d{6}$/.test(typed.replace(/\s+/g, ''))
          ? { code: typed.replace(/\s+/g, '') }
          : { recoveryCode: typed },
      );
      setMfaPw('');
      setMfaCode('');
      setMfaStage('idle');
      setMfaNotice('Two-factor authentication is off.');
      refreshMfa();
    } catch (e) {
      mfaError(e, 'network error — is the server running?');
    } finally {
      setBusy(false);
    }
  };
  // The EMAIL-OTP tier (AD-332 item 2; design §7b): enrolment mails an 8-digit
  // code; disable mails a fresh one first (`codeRequired`). TOTP outranks it,
  // so the enrol option shows only while no tier is on.
  const startEmailMfa = async (): Promise<void> => {
    setBusy(true);
    setError('');
    setMfaNotice('');
    try {
      await mfaEmailRequest(mfaPw);
      setMfaPw('');
      setMfaCode('');
      setMfaStage('email-confirm');
      setMfaNotice('We emailed you an 8-digit code — enter it below.');
    } catch (e) {
      mfaError(e, 'network error — is the server running?');
    } finally {
      setBusy(false);
    }
  };
  const confirmEmailMfa = async (): Promise<void> => {
    setBusy(true);
    setError('');
    try {
      await mfaEmailConfirm(mfaCode.replace(/\s+/g, ''));
      setMfaCode('');
      setMfaStage('idle');
      setMfaNotice('Email sign-in codes are on. Your other devices have been signed out.');
      refreshMfa();
    } catch (e) {
      mfaError(e, 'network error — is the server running?');
    } finally {
      setBusy(false);
    }
  };
  const disableEmailMfa = async (): Promise<void> => {
    setBusy(true);
    setError('');
    setMfaNotice('');
    try {
      const typed = mfaCode.replace(/\s+/g, '');
      const res = await mfaEmailDisable(mfaPw, typed.length > 0 ? typed : undefined);
      if (res.codeRequired) {
        setMfaNotice('We emailed you a code — enter it with your password to turn this off.');
        return;
      }
      setMfaPw('');
      setMfaCode('');
      setMfaStage('idle');
      setMfaNotice('Email sign-in codes are off.');
      refreshMfa();
    } catch (e) {
      mfaError(e, 'network error — is the server running?');
    } finally {
      setBusy(false);
    }
  };
  const changePw = async (): Promise<void> => {
    setBusy(true);
    setError('');
    setPwNotice('');
    try {
      await changePassword(currentPw, newPw);
      setCurrentPw('');
      setNewPw('');
      setPwOpen(false);
      setPwNotice('Password changed. Your other devices have been signed out.');
    } catch (e) {
      if (e instanceof ApiError && e.code === 'authFailed')
        setError('Current password is incorrect.');
      else if (e instanceof ApiError && e.code === 'badPassword')
        setError('Choose a new password of 8 to 128 characters.');
      else setError(e instanceof ApiError ? e.message : 'network error — is the server running?');
    } finally {
      setBusy(false);
    }
  };

  const create = async (): Promise<void> => {
    setBusy(true);
    setError('');
    try {
      const res = await createCharacter(token, name, appearance);
      setName('');
      onCreated(res.character);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="screen">
      <div className="panel">
        <h1>Choose a character</h1>
        <PrestigeOfferPrompt />
        {/* `.charlist` + this testid are what automation waits on to know character
            select is ready and which buttons are characters (visual-lane login.mjs;
            the weekly capture drives the same DOM). Keep them on THIS list only. */}
        <div className="charlist" data-testid="character-list">
          {characters.map((c) => (
            <button key={c.id} onClick={() => onEnter(c.id)}>
              {c.name}
            </button>
          ))}
          {characters.length === 0 ? (
            <div className="sub">No characters yet — create one:</div>
          ) : null}
        </div>
        {characters.length < 3 ? (
          <>
            <input
              placeholder="new character name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <div className="sub">Body build</div>
            <div className="optionlist">
              {BODY_TYPES.map((b) => (
                <button
                  key={b}
                  className={appearance.bodyType === b ? '' : 'secondary'}
                  style={{ textTransform: 'capitalize' }}
                  onClick={() => setAppearance((a) => ({ ...a, bodyType: b }))}
                >
                  {b}
                </button>
              ))}
            </div>
            {wardrobe ? (
              <>
                <select
                  value={appearance.face}
                  onChange={(e) => setAppearance((a) => ({ ...a, face: e.target.value }))}
                >
                  {wardrobe.options
                    .filter((o) => o.slot === 'face')
                    .map((o) => (
                      <option key={o.id} value={o.id}>
                        Face: {o.name}
                      </option>
                    ))}
                </select>
                <select
                  value={appearance.hair}
                  onChange={(e) => setAppearance((a) => ({ ...a, hair: e.target.value }))}
                >
                  {wardrobe.options
                    .filter((o) => o.slot === 'hair')
                    .map((o) => (
                      <option key={o.id} value={o.id}>
                        Hair: {o.name}
                      </option>
                    ))}
                </select>
                <select
                  value={appearance.skin}
                  onChange={(e) => setAppearance((a) => ({ ...a, skin: e.target.value }))}
                >
                  {wardrobe.options
                    .filter((o) => o.slot === 'skin')
                    .map((o) => (
                      <option key={o.id} value={o.id}>
                        Skin: {o.name}
                      </option>
                    ))}
                </select>
              </>
            ) : null}
            <button
              className="secondary"
              disabled={busy || name.length < 3}
              onClick={() => void create()}
            >
              Create character
            </button>
          </>
        ) : null}
        <div className="error">{error}</div>
        {pwNotice ? <div className="sub">{pwNotice}</div> : null}
        {pwOpen ? (
          <>
            <input
              placeholder="current password"
              type="password"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              autoComplete="current-password"
            />
            <input
              placeholder="new password (8+ characters)"
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              autoComplete="new-password"
            />
            <button
              className="secondary"
              disabled={busy || currentPw.length === 0 || newPw.length < 8}
              onClick={() => void changePw()}
            >
              Save new password
            </button>
          </>
        ) : null}
        <button
          className="secondary"
          disabled={busy}
          onClick={() => {
            setPwOpen(!pwOpen);
            setError('');
            setPwNotice('');
          }}
        >
          {pwOpen ? 'Cancel' : 'Change password'}
        </button>
        {mfaNotice ? <div className="sub">{mfaNotice}</div> : null}
        {mfaOpen ? (
          <>
            <div className="sub">
              {mfa === null
                ? 'Two-factor authentication'
                : !mfa.available
                  ? 'Two-factor authentication is not available on this server yet.'
                  : mfa.enrolled
                    ? `Two-factor authentication is ON (${mfa.recoveryCodesRemaining} recovery codes left).`
                    : 'Two-factor authentication is OFF. Protect this account with an authenticator app.'}
            </div>
            {mfaStage === 'idle' && mfa?.available && !mfa.enrolled ? (
              <>
                <input
                  placeholder="your password"
                  type="password"
                  value={mfaPw}
                  onChange={(e) => setMfaPw(e.target.value)}
                  autoComplete="current-password"
                />
                <button
                  className="secondary"
                  disabled={busy || mfaPw.length === 0}
                  onClick={() => void startMfa()}
                >
                  Set up two-factor authentication
                </button>
              </>
            ) : null}
            {mfaStage === 'setup' && mfaSecret ? (
              <>
                <div className="sub">
                  Open your authenticator app and{' '}
                  <a href={mfaSecret.otpauthUri}>add this account</a>, or enter the key by hand:
                </div>
                <code style={{ wordBreak: 'break-all', userSelect: 'all' }}>
                  {mfaSecret.secret}
                </code>
                <div className="sub">Then enter the 6-digit code the app shows:</div>
                <input
                  placeholder="6-digit code"
                  value={mfaCode}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  onChange={(e) => setMfaCode(e.target.value)}
                />
                <button
                  className="secondary"
                  disabled={busy || mfaCode.trim().length < 6}
                  onClick={() => void confirmMfa()}
                >
                  Confirm and turn on
                </button>
              </>
            ) : null}
            {mfaStage === 'codes' ? (
              <>
                <div className="sub">
                  Save these recovery codes somewhere safe — each works ONCE if you lose your
                  authenticator, and they will not be shown again:
                </div>
                <code style={{ whiteSpace: 'pre-wrap', userSelect: 'all' }}>
                  {recoveryCodes.join('\n')}
                </code>
                <button
                  className="secondary"
                  disabled={busy}
                  onClick={() => {
                    setRecoveryCodes([]);
                    setMfaStage('idle');
                  }}
                >
                  I have saved them
                </button>
              </>
            ) : null}
            {mfaStage === 'idle' && mfa?.enrolled ? (
              <button
                className="secondary"
                disabled={busy || mfa.moderator}
                title={mfa.moderator ? 'Moderator accounts must keep two-factor on' : undefined}
                onClick={() => {
                  setMfaStage('disable');
                  setMfaPw('');
                  setMfaCode('');
                  setError('');
                }}
              >
                Turn off two-factor authentication
              </button>
            ) : null}
            {mfaStage === 'disable' ? (
              <>
                <input
                  placeholder="your password"
                  type="password"
                  value={mfaPw}
                  onChange={(e) => setMfaPw(e.target.value)}
                  autoComplete="current-password"
                />
                <input
                  placeholder="6-digit code or a recovery code"
                  value={mfaCode}
                  autoComplete="one-time-code"
                  onChange={(e) => setMfaCode(e.target.value)}
                />
                <button
                  className="secondary"
                  disabled={busy || mfaPw.length === 0 || mfaCode.trim().length === 0}
                  onClick={() => void disableMfa()}
                >
                  Turn off
                </button>
                <button
                  className="secondary"
                  disabled={busy}
                  onClick={() => {
                    setMfaStage('idle');
                    setError('');
                  }}
                >
                  Keep it on
                </button>
              </>
            ) : null}
            {mfa?.emailAvailable ? (
              <div className="sub">
                {mfa.emailEnrolled
                  ? 'Email sign-in codes are ON.'
                  : 'Email sign-in codes are OFF — the simplest second factor; an authenticator app is stronger.'}
              </div>
            ) : null}
            {mfaStage === 'idle' && mfa?.emailAvailable && mfa.tier === 'none' ? (
              <>
                <input
                  placeholder="your password"
                  type="password"
                  value={mfaPw}
                  onChange={(e) => setMfaPw(e.target.value)}
                  autoComplete="current-password"
                />
                <button
                  className="secondary"
                  disabled={busy || mfaPw.length === 0}
                  onClick={() => void startEmailMfa()}
                >
                  Use email sign-in codes
                </button>
              </>
            ) : null}
            {mfaStage === 'email-confirm' ? (
              <>
                <input
                  placeholder="8-digit code from your email"
                  value={mfaCode}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  onChange={(e) => setMfaCode(e.target.value)}
                />
                <button
                  className="secondary"
                  disabled={busy || mfaCode.trim().length < 8}
                  onClick={() => void confirmEmailMfa()}
                >
                  Confirm and turn on
                </button>
              </>
            ) : null}
            {mfaStage === 'idle' && mfa?.emailEnrolled ? (
              <button
                className="secondary"
                disabled={busy || (mfa.moderator && !mfa.enrolled)}
                title={
                  mfa.moderator && !mfa.enrolled
                    ? 'Moderator accounts must keep two-factor on'
                    : undefined
                }
                onClick={() => {
                  setMfaStage('email-disable');
                  setMfaPw('');
                  setMfaCode('');
                  setError('');
                  setMfaNotice('');
                }}
              >
                Turn off email sign-in codes
              </button>
            ) : null}
            {mfaStage === 'email-disable' ? (
              <>
                <input
                  placeholder="your password"
                  type="password"
                  value={mfaPw}
                  onChange={(e) => setMfaPw(e.target.value)}
                  autoComplete="current-password"
                />
                <input
                  placeholder="8-digit code (emailed after the first step)"
                  value={mfaCode}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  onChange={(e) => setMfaCode(e.target.value)}
                />
                <button
                  className="secondary"
                  disabled={busy || mfaPw.length === 0}
                  onClick={() => void disableEmailMfa()}
                >
                  {mfaCode.trim().length > 0 ? 'Turn off' : 'Email me a code'}
                </button>
                <button
                  className="secondary"
                  disabled={busy}
                  onClick={() => {
                    setMfaStage('idle');
                    setError('');
                  }}
                >
                  Keep it on
                </button>
              </>
            ) : null}
          </>
        ) : null}
        <button
          className="secondary"
          disabled={busy}
          onClick={() => {
            setMfaOpen(!mfaOpen);
            setMfaStage('idle');
            setMfaSecret(null);
            setRecoveryCodes([]);
            setError('');
            setMfaNotice('');
            if (!mfaOpen) refreshMfa();
          }}
        >
          {mfaOpen ? 'Close two-factor settings' : 'Two-factor authentication'}
        </button>
        <button className="secondary" onClick={onLogout}>
          Log out
        </button>
      </div>
    </div>
  );
}

function GameScreen({
  token,
  characterId,
  onLeave,
  guest,
  onKicked,
}: {
  token: string;
  characterId: number;
  onLeave: () => void;
  /**
   * The guest demo (AD-794 §6.2): present only for a `?guest=1` session on the guest origin. The
   * token is the in-memory guest bearer (never `gc.token`); the end triggers (final turn-in, the
   * fixed TTL, a failed connection) call `guest.onEnd`; a `guest_wall` refusal is one calm toast.
   */
  guest?: GuestGameProps;
  /**
   * The server kicked this connection (q-duplicate-login-warning routes two reasons to login).
   * Absent for a guest: a guest's failed connection already ends the demo (AD-794 §6.2).
   */
  onKicked?: (reason: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const gameRef = useRef<Game | null>(null);
  const connRef = useRef<GameConnection | null>(null);
  const [status, setStatus] = useState<GameStatus | null>(null);
  // E1.5: expose the live Game to the capture controller once it is created.
  const [game, setGame] = useState<Game | null>(null);
  const [store] = useState(() => new ClientStore());
  // A boss bark's subtitle (AD-759): the spoken words as a toast, `Speaker: "words"`.
  useEffect(
    () => subscribeBarks((who, text) => store.toast('info', `${who}: “${text}”`, 'bark')),
    [store],
  );
  // q-no-device-lost-recovery-either-renderer: the renderer died after start.
  const [rendererLost, setRendererLost] = useState<RendererLossInfo | null>(null);
  // q-website-review-v4-fixes B4: the renderer never started (no WebGL/WebGPU context, or the
  // Game constructor threw). Without this the loading screen held forever with no way out.
  const [rendererStartFailed, setRendererStartFailed] = useState(false);
  // q-asset-readiness-loading-screen (AD-794): the loading screen holds from this mount until
  // what is in view has loaded (or READINESS_TIMEOUT_MS). One session per game view.
  const [readiness] = useState(() => new ReadinessSession({ startMs: performance.now() }));
  const [revealed, setRevealed] = useState(false);
  // AD-794: the guest props, read through a ref so the connection effect never re-runs for them.
  const guestRef = useRef<GuestGameProps | undefined>(guest);
  guestRef.current = guest;
  const [walls] = useState(() => new WallNotifier());

  // AD-794 §6.2/§6.4: the guest's end triggers — the fixed TTL, and the final quest's turn-in.
  const guestExpiresAt = guest?.session.expiresAt;
  const guestFinalQuest = guest?.finalQuestId ?? null;
  useEffect(() => {
    if (guestExpiresAt === undefined) return undefined;
    const timer = window.setTimeout(
      () => guestRef.current?.onEnd('expired'),
      msUntilExpiry(guestExpiresAt, Date.now()),
    );
    let completion: number | null = null;
    const unsubscribe = store.subscribe(() => {
      if (completion !== null) return;
      if (finalQuestTurnedIn(store.state.quests?.quests, guestFinalQuest)) {
        completion = window.setTimeout(
          () => guestRef.current?.onEnd('completed'),
          COMPLETION_CARD_DELAY_MS,
        );
      }
    });
    return () => {
      window.clearTimeout(timer);
      if (completion !== null) window.clearTimeout(completion);
      unsubscribe();
    };
  }, [guestExpiresAt, guestFinalQuest, store]);
  // A failed connection ends the demo: the instance went with it (§6.2 "a lapsed guest reloads
  // into a NEW guest").
  const statusPhase = status?.phase;
  const statusPhaseDetail = status?.phaseDetail;
  useEffect(() => {
    if (statusPhase !== 'failed' || guestExpiresAt === undefined) return;
    console.info(`[guest] connection failed: ${statusPhaseDetail ?? 'no detail'}`);
    guestRef.current?.onEnd(endReasonForFailure(guestExpiresAt, Date.now()));
  }, [statusPhase, statusPhaseDetail, guestExpiresAt]);
  // q-duplicate-login-warning: the connection's own phase, for the "already logged in" choice.
  const [conflict, setConflict] = useState<{ busy: boolean; rateLimited: boolean } | null>(null);
  const onKickedRef = useRef(onKicked);
  onKickedRef.current = onKicked;

  // Publish to tooling (`window.__tgcReady`, `__tgcReadyInfo`, `html[data-tgc-ready]`) and run
  // the wall-clock watchdog: the timeout must fire even while the frame loop is stalled.
  useEffect(() => {
    const target = window as unknown as ReadinessGlobals;
    const root = document.documentElement;
    publishReadiness(readiness.state, false, target, root);
    const unsubscribe = readiness.subscribe((state) =>
      publishReadiness(state, false, target, root),
    );
    const watchdog = window.setInterval(() => {
      if (readiness.ready) window.clearInterval(watchdog);
      else readiness.tick(performance.now());
    }, 500);
    return () => {
      unsubscribe();
      window.clearInterval(watchdog);
      target.__tgcReady = false; // the game view is gone: nothing is ready to film
      root.setAttribute(READY_ATTRIBUTE, 'false');
    };
  }, [readiness]);
  const onRevealed = useCallback(() => {
    setRevealed(true);
    publishReadiness(
      readiness.state,
      true,
      window as unknown as ReadinessGlobals,
      document.documentElement,
    );
  }, [readiness]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let game: Game | null = null;
    let disposed = false;
    const packStore = new ContentPackStore();
    const channels = channelHooks(token, { guest: guestRef.current !== undefined });
    const conn = new GameConnection(
      token,
      characterId,
      {
        onReady: (ack, resumed) => {
          // The frame loop starts HERE, on the helloAck, never before `conn.connect()`
          // (q-ci-nightly-main-thread-stall-since-loading-screen): the first rendered frame
          // compiles the sky, ground and post-FX programs synchronously, which on a software
          // renderer (SwiftShader — every CI runner) or a slow phone blocks the main thread
          // for tens of seconds. Started ahead of the socket, that block kept the `open`
          // handler from sending `hello` inside the server's 10 s deadline (wsServer
          // `helloTimeout`), the client retried five times into the same blocks and ended
          // `phase=failed`. With the ack in hand the world is here, the loading screen is
          // already covering the first frames, and `start()` is idempotent on a resume.
          game?.start();
          game?.onReady(ack, resumed);
          // P4.9: refetch the presentation pack if our cached hash is stale.
          void packStore
            .sync(ack.contentPackHash)
            .then(() => {
              game?.onContentPack(packStore.hash, packStore.pack !== null);
              // q-player-names-and-wayfinding: every player-facing id resolves to its content name.
              setContentNames(packStore.pack);
              setWayfindingContent(packStore.pack);
              // AD-665: the gather channel's length is per gathering skill.
              store.setResourceSkills(
                Object.fromEntries(
                  Object.values(packStore.pack?.categories.resources ?? {}).map((r) => [
                    (r as { id: string }).id,
                    String((r as { gatheringSkill?: unknown }).gatheringSkill ?? ''),
                  ]),
                ),
              );
              // AD-794 §6.2: the guest's [E] prompt skips nodes its ranks cannot open.
              store.setResourceTiers(
                Object.fromEntries(
                  Object.values(packStore.pack?.categories.resources ?? {}).map((r) => [
                    (r as { id: string }).id,
                    Number((r as { tier?: unknown }).tier ?? 0),
                  ]),
                ),
              );
              // sprint-assembly-1: the crafting panel's "Craftable now" reads the recipes.
              store.setRecipes(
                Object.values(
                  packStore.pack?.categories.recipes ?? {},
                ) as unknown as CraftRecipeView[],
              );
            })
            .catch((err: unknown) => {
              console.warn('content pack sync failed', err);
              game?.onContentPack(ack.contentPackHash, false);
            });
        },
        onSnapshot: (tick, snap) => game?.onSnapshot(tick, snap),
        onEvent: (tick, seq, ev) => game?.onEvent(tick, seq, ev),
        onPhase: (phase, detail) => {
          game?.onPhase(phase, detail);
          if (phase === 'conflict')
            setConflict({ busy: false, rateLimited: detail === 'rateLimited' });
          else if (phase === 'playing' || phase === 'closed' || phase === 'failed')
            setConflict(null);
        },
        onKicked: (reason) => onKickedRef.current?.(reason),
        onRtt: (rtt) => game?.onRtt(rtt),
        onNotice: (message) => game?.onNotice(message),
        // AD-794 §3.2: a walled demo press is one calm toast, never an error string per press.
        onGuestWall: (type) => {
          const line = walls.note(type, Date.now());
          if (line !== null) store.toast('info', line);
        },
        onAny: (msg) => {
          store.apply(msg);
          // E4.6.S3 slice 2d: the run swap is the Game's (the store only toasts it).
          if (msg.t === 'dungeonResult') game?.onDungeonResult(msg.d);
          // AD-817: a held press on a hostile fires weapon ability 1 — the Game needs the loadout.
          if (msg.t === 'loadoutState') game?.onLoadout(msg.d);
          // AD-867: a quest ready to hand in marks its turn-in NPC with a '?' in the world.
          if (msg.t === 'questLog') game?.onQuestLog(msg.d);
        },
      },
      () => packStore.hash,
      // D1: `?snaphz=10|15` asks for a snapshot rate (the owner's 10-vs-15 playtest).
      requestedSnapshotHz(effectiveSearch()),
      // D4: advertise binary snapshots unless `?snapbin=0`.
      binarySnapshotsWanted(effectiveSearch()),
      // A8b: ask which channel process to open (`/ws/c<n>`); any failure opens `/ws`. A resume
      // whose channel process is gone asks again instead of retrying a dead URL. A guest never
      // asks (its origin has no channels).
      channels.helloUrl,
      channels.channelAsk,
    );
    // WR.3.3: resolve the descriptor's theme/assetPack ids from the loaded
    // content pack (its themes + asset-packs categories project the presentation
    // fields the scene builder needs — client-pack.ts PRESENTATION_FIELDS).
    const content: SceneContentLookup = {
      theme: (id) => packStore.get('themes', id) as unknown as ThemeDef | undefined,
      assetPack: (id) => packStore.get('asset-packs', id) as unknown as AssetPackDef | undefined,
      // AD-315: the joined zone's biome def carries the sub-projected
      // `generation` the scene derives its dressing from. Left untyped — it is
      // pack JSON, narrowed defensively by `clientGenerationOf`.
      biome: (id) => packStore.get('biomes', id) as unknown as ClientPackDef | undefined,
      // AD-408: the bound terrain set (elevation only) the ground is derived from.
      // ON by default (`q-follow-camera-vs-relief`): the follow camera now frames a
      // slope (terrainCamera.ts), so the URL opt-in that kept every world flat is gone;
      // `?terrain=0` ("Flat ground" in the playtest panel) is the dev escape. No set ⇒
      // the flat ground, as before.
      terrainSet: (id) =>
        terrainReliefOn(effectiveSearch()) ? packStore.get('terrain-tiles', id) : undefined,
    };
    // E2.2.S2: self-view equipment presentation. Item + asset-pack presentation
    // resolve from the same content pack; the equipped set + its change signal
    // come from the store's inventory-snapshot slice (the one the character
    // panels read). Self-view only — EntityState carries no equipment field, so
    // other entities' gear is out of scope by construction.
    const equipmentContent: EquipmentContent = {
      itemPresentation: (id) =>
        (packStore.get('items', id) as unknown as { presentation?: PresentationRef } | undefined)
          ?.presentation,
      assetPack: (id) => packStore.get('asset-packs', id) as unknown as AssetPackDef | undefined,
      // Weapon-skin overlay (weapon-skins.md): the client-pack `cosmetics`
      // projection (kind/slot/appliesTo/presentation) the resolve chain checks a
      // preferred skin against. Self-view only; the preference never rides the wire.
      cosmetic: (id) => packStore.get('cosmetics', id) as unknown as ClientCosmetic | undefined,
    };
    const equipped: EquippedItemsSource = {
      get: () => equippedItemsForCharacter(store.state.inventory?.items ?? [], characterId),
      subscribe: store.subscribe,
    };
    const equipment: EquipmentBridge = { content: equipmentContent, equipped };
    // E2.2.S3: self-view gather-tool held prop. Reuses `equipmentContent`
    // (structurally a `GatherToolContent` — it exposes `assetPack`); the gather
    // signal is the store's `lastGather` slice, set from each `gatherResult`
    // (carrying `gatheringSkill` on success), so nothing new rides the wire.
    const gatherTool: GatherToolBridge = {
      content: equipmentContent,
      // AD-665: shown for the gather CHANNEL (from the press), not after the yield.
      gather: { get: () => store.state.gatherChannel, subscribe: store.subscribe },
    };
    // E2.3.S2: enemy creature presentation. Enemies resolve client-side BY NAME
    // (the wire carries no enemy contentId), so the bridge exposes the client-pack
    // `enemies` projection (name + presentation) for the reverse map plus the
    // `asset-packs` lookup. Reuses the same content pack — nothing new on the wire.
    const creatureContent: CreatureContent = {
      enemies: () =>
        Object.values(packStore.pack?.categories.enemies ?? {}) as unknown as EnemyPresentation[],
      assetPack: (id) => packStore.get('asset-packs', id) as unknown as AssetPackDef | undefined,
      // q-creature-maps-stale-after-pack-swap: a reconnect onto a new hash rebuilds the maps.
      revision: () => packStore.hash,
    };
    const creature: CreatureBridge = { content: creatureContent };
    // E2.4.S2: player rig avatars. The rig pack + the appearance-option catalogue
    // resolve from the same content pack (asset-packs + appearance-options project the
    // presentation fields — client-pack.ts PRESENTATION_FIELDS); appearance rides the
    // existing full-state field (AD-137), so nothing new is on the wire.
    const rigContent: RigContent = {
      assetPack: (id) => packStore.get('asset-packs', id) as unknown as AssetPackDef | undefined,
      appearanceOption: (id) =>
        packStore.get('appearance-options', id) as unknown as AppearanceOptionDef | undefined,
    };
    // AP5.S2: the own rig's gather/craft one-shots key off the store's `lastGather`
    // / `lastCraft` slices (set per applied `gatherResult` / `craftResult`), so
    // nothing new rides the wire; combat one-shots come off the event channel.
    const rig: RigBridge = {
      content: rigContent,
      moments: {
        // AD-665: the gather clip plays DURING the channel (from the press), fitted to it.
        gather: { get: () => store.state.gatherChannel, subscribe: store.subscribe },
        craft: { get: () => store.state.lastCraft, subscribe: store.subscribe },
      },
    };
    // E2.4.S3: NPC in-world placement. NPCs resolve id → client-pack npc def →
    // presentation (0 of 13 carry one today) → else the humanoid rig pack with
    // DEFAULT_APPEARANCE; `helloAck.npcs` is join-only + already delivered, so nothing
    // new rides the wire. Extends the rig content (asset-packs + appearance-options)
    // with the `npcs` projection lookup.
    const npcContent: NpcContent = {
      ...rigContent,
      npc: (id) => packStore.get('npcs', id) as unknown as NpcPresentation | undefined,
    };
    const npc: NpcBridge = { content: npcContent };
    // E3.4.S3: resource-node in-world placement (the AD-141 node->asset seam). A node
    // resolves resource id -> the client-pack `resources` projection's `gatheringSkill`
    // -> RESOURCE_NODE_ASSET archetype -> the delivered kit that carries it. The biome's
    // `generation.nodeKit` is deliberately NOT in the client pack (client-pack.ts strips
    // it), so the kit is FOUND among delivered packs rather than named; `helloAck.nodes`
    // is join-only and already delivered, so nothing new rides the wire.
    const nodeContent: NodeContent = {
      resource: (id) =>
        packStore.get('resources', id) as unknown as ResourcePresentation | undefined,
      assetPacks: () =>
        Object.values(packStore.pack?.categories['asset-packs'] ?? {}) as unknown as AssetPackDef[],
    };
    const node: NodeBridge = { content: nodeContent };
    // WGP-001b: the adapter probe + WebGPURenderer.init() are asynchronous.
    // Construct and inject the renderer before starting either the Game or its
    // connection; a teardown during the probe disposes the unused GPU object.
    const construct = (createdRenderer: CreatedRenderer): Game => {
      try {
        return new Game(
          container,
          conn,
          setStatus,
          createdRenderer,
          content,
          equipment,
          gatherTool,
          creature,
          rig,
          npc,
          node,
          readiness,
        );
      } catch (error) {
        // Ownership changes only after construction succeeds. A constructor
        // failure may already have appended the canvas or built GPU state.
        disposeCreatedRenderer(createdRenderer);
        throw error;
      }
    };
    void createGameRenderer()
      .then(async (createdRenderer) => {
        if (disposed) {
          disposeCreatedRenderer(createdRenderer);
          return;
        }
        try {
          game = construct(createdRenderer);
        } catch (error) {
          if (createdRenderer.kind !== 'webgpu') throw error;
          // AD-9020: WebGPU is the default, so a game that cannot start on it gets one retry
          // on the WebGL2 fallback rather than the "couldn't start the 3D graphics" notice.
          console.warn('[renderer] the game failed to start on WebGPU; retrying on WebGL2', error);
          const fallback = await createGameRenderer({ forceWebGl: true });
          if (disposed) {
            disposeCreatedRenderer(fallback);
            return;
          }
          game = construct(fallback);
        }
        gameRef.current = game;
        connRef.current = conn;
        setGame(game);
        game.onRendererLost((info) => {
          if (!disposed) setRendererLost(info);
        });
        // Not `game.start()` here: the loop starts on the helloAck (see `onReady` above), so the
        // first frame's shader compiles can never delay the handshake.
        conn.connect();
      })
      .catch((error: unknown) => {
        if (disposed) return;
        console.error('renderer initialization failed', error);
        setRendererStartFailed(true);
      });
    return () => {
      disposed = true;
      conn.close();
      game?.dispose();
      gameRef.current = null;
      connRef.current = null;
      setGame(null);
      setRendererLost(null);
      setRendererStartFailed(false);
      store.reset();
    };
  }, [token, characterId, store, readiness, walls]);

  return (
    <div className="game-root" ref={containerRef}>
      {revealed || rendererStartFailed ? null : (
        <LoadingScreen session={readiness} onRevealed={onRevealed} />
      )}
      {rendererStartFailed ? <RendererStartFailedNotice /> : null}
      {status && connRef.current ? (
        <NetProvider store={store} conn={connRef.current}>
          <CaptureController
            game={game}
            status={status}
            onLeave={guest ? () => guest.onEnd('left') : onLeave}
          />
        </NetProvider>
      ) : null}
      {rendererLost ? <RendererLostNotice /> : null}
      {conflict ? (
        <DuplicateLoginDialog
          busy={conflict.busy}
          rateLimited={conflict.rateLimited}
          onBackOut={() => {
            connRef.current?.backOut();
            onLeave();
          }}
          onTakeOver={() => {
            setConflict({ busy: true, rateLimited: false });
            connRef.current?.takeOver();
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Shown when the renderer dies after start (GPU reset, driver hang, context
 * loss). The picture stopped; the server-side world did not — so say that
 * plainly, and offer the one recovery that is safe on any GPU: a reload.
 */
function RendererLostNotice() {
  return (
    <div className="renderer-lost" role="alert">
      <h2>The picture stopped</h2>
      <p>
        Your graphics device stopped responding, so the game can no longer draw. This is a display
        problem on your device — the game world is unaffected.
      </p>
      <button type="button" onClick={() => window.location.reload()}>
        Reload to continue
      </button>
    </div>
  );
}

export default function App() {
  const [screen, setScreen] = useState<Screen>({ kind: 'auth' });
  // AD-794 §6.2: `?guest=1` is the `try.` host's "Play now" boot path. Where the demo is not
  // served (the main origin answers its routes 404) it falls back to the auth screen + a notice.
  const [guestBoot, setGuestBoot] = useState<'guest' | 'unavailable' | 'off'>(() =>
    guestBootRequested(window.location.search) ? 'guest' : 'off',
  );
  const onGuestUnavailable = useCallback(() => setGuestBoot('unavailable'), []);

  // Try to restore the stored session on first load.
  useEffect(() => {
    // q-playtest-cross-zone-jump: a dev reload stays on the zone backend the last jump chose,
    // before the first request (the token was issued there).
    if (devToolsEnabled()) {
      try {
        restoreZoneRoute(routedDevZones(), window.location.origin, window.sessionStorage);
      } catch {
        /* sessionStorage unavailable — the default server it is */
      }
    }
    // A guest boot never restores (or reads) a stored session: the guest token is page memory.
    if (guestBootRequested(window.location.search)) return;
    const token = getToken();
    if (!token) return;
    void listCharacters(token)
      .then((res) => setScreen({ kind: 'select', token, characters: res.characters }))
      .catch(() => clearToken());
  }, []);

  // O-025/O-026: techdemo playtest jump hook — re-login as a seeded character
  // standing at a point of interest (the AD-216 capture-set "teleport" pattern;
  // the normal login/select flow, nothing new on the wire). Consumed by the
  // Playtest panel AND by CloudMediaStudio's capture-plan executor, so it is kept
  // on `window` rather than passed as a prop.
  //
  // Gate (O-026): DEV BUILDS ONLY. Was `?devmenu=1`; now `import.meta.env.DEV`,
  // a Vite compile-time literal — production builds cannot register it at all,
  // rather than merely declining to.
  useEffect(() => {
    if (!devToolsEnabled()) return;
    const jump = async (email: string, password: string, character: string): Promise<boolean> => {
      try {
        const res = await login(email, password);
        // An MFA-enrolled seeded account cannot be jumped into (no second factor here).
        if (isMfaChallenge(res)) return false;
        setToken(res.token);
        const target = res.characters.find((entry) => entry.name === character);
        // Force a clean remount of the game screen even when already in-game.
        setScreen({ kind: 'select', token: res.token, characters: res.characters });
        if (target) {
          setScreen({ kind: 'game', token: res.token, characterId: target.id });
          return true;
        }
        return false;
      } catch {
        return false;
      }
    };
    (window as unknown as Record<string, unknown>).__GC_DEVJUMP = jump;

    // q-visual-lane-no-dungeon-route: the SAME gate, for the one affordance the
    // visual lane could not reach at all. `DungeonWorldLayer` hangs off
    // `devBridge()`, a MODULE SINGLETON — invisible to a CDP driver, which can
    // only see `window`. The PlaytestPanel reaches it by import; the lane
    // cannot, so a `footprint.frontend` change to the dungeon interior had no
    // way to discharge its AD-300 visual-lane duty. This mirrors `__GC_DEVJUMP`
    // exactly: dev builds only, one narrow function, deleted on unmount, and it
    // exposes NO object — the layer, the renderer and the socket stay unreachable.
    const enterDungeon = async (difficultyId: string, options?: unknown): Promise<boolean> => {
      // S4 options, validated here like `__GC_DEVCAM`'s op: a malformed option
      // is a refusal, not a silently ignored key.
      const opts = parseDevDungeonOptions(options);
      if (!opts) return false;
      const layer = devBridge()?.dungeonLayer();
      // No layer means `?dungeons` is off (or no Game is mounted). Returning
      // false rather than throwing keeps the caller's failure legible: the lane
      // reports a waypoint finding, not a CDP exception.
      if (!layer) return false;
      // REFUSE A DIFFICULTY THIS ZONE DOES NOT HAVE, rather than entering a
      // different one. `previewDifficulty` ends `?? this.sites[0]`, so asking a
      // zone for a band it was not allotted silently enters whatever shrine
      // happens to be first — and for the visual lane that is the worst possible
      // outcome: a waypoint that PASSES while reporting on a dungeon it did not
      // ask for. Shrine placement is deterministic (`TECHDEMO_WORLD_SEED`) and
      // filtered per zone, so 'mythic' simply does not exist in
      // `the-verdant-reach`. Checked HERE, in the dev hook, so the layer's own
      // forgiving panel behaviour is left exactly as it was.
      if (!layer.listSites().some((site) => site.difficultyId === difficultyId)) return false;
      try {
        if (!(await layer.previewDifficulty(difficultyId, opts.cycle))) return false;
        if (opts.at !== undefined && !layer.placePreviewAt(opts.at)) return false;
        return opts.present === undefined || layer.setPreviewPresentation(opts.present);
      } catch {
        return false;
      }
    };
    (window as unknown as Record<string, unknown>).__GC_DEVDUNGEON = enterDungeon;

    // q-capture-camera-channel-devcam-bridge: the camera channel the studio's
    // `cam:` step needs — until now the camera was set once, before load, and
    // nothing could move it mid-shot. Same gate, same lifetime, same `false`
    // contract; the op is validated HERE against CAMERA_BOUNDS (cameraMove.ts)
    // so an out-of-envelope target is refused before the Game ever sees it.
    const moveCamera = async (op: unknown): Promise<boolean> => {
      const move = parseCameraMove(op);
      if (!move) return false;
      return devBridge()?.cameraMove(move) ?? false;
    };
    (window as unknown as Record<string, unknown>).__GC_DEVCAM = moveCamera;

    return () => {
      delete (window as unknown as Record<string, unknown>).__GC_DEVJUMP;
      delete (window as unknown as Record<string, unknown>).__GC_DEVDUNGEON;
      delete (window as unknown as Record<string, unknown>).__GC_DEVCAM;
    };
  }, []);

  const logout = useCallback(() => {
    // Server first (deletes exactly the calling session row — AD-314 §3.2 D),
    // then local; a failed server call never keeps the player logged in here.
    if (getToken()) void logoutServer().catch(() => undefined);
    clearToken();
    setScreen({ kind: 'auth' });
  }, []);

  if (guestBoot === 'guest') {
    return (
      <GuestApp
        onUnavailable={onGuestUnavailable}
        renderGame={(props) => (
          <GameScreen
            key={props.session.token}
            token={props.session.token}
            characterId={props.session.characterId}
            onLeave={() => props.onEnd('left')}
            guest={props}
          />
        )}
      />
    );
  }

  if (screen.kind === 'auth') {
    return (
      <AuthScreen
        initialNotice={
          screen.notice ??
          (guestBoot === 'unavailable' ? "The demo isn't available here — log in or sign up." : '')
        }
        onAuthed={(token, characters) => setScreen({ kind: 'select', token, characters })}
      />
    );
  }
  if (screen.kind === 'select') {
    return (
      <SelectScreen
        token={screen.token}
        characters={screen.characters}
        onLogout={logout}
        onCreated={(c) =>
          setScreen({ kind: 'select', token: screen.token, characters: [...screen.characters, c] })
        }
        onEnter={(characterId) => setScreen({ kind: 'game', token: screen.token, characterId })}
      />
    );
  }
  return (
    <GameScreen
      token={screen.token}
      characterId={screen.characterId}
      onKicked={(reason) => {
        const route = kickedRoute(reason);
        if (!route) return; // any other kick keeps the game screen's own failed state
        if (route.clearToken) clearToken();
        setScreen({ kind: 'auth', notice: route.notice });
      }}
      onLeave={() =>
        void listCharacters(screen.token)
          .then((res) =>
            setScreen({ kind: 'select', token: screen.token, characters: res.characters }),
          )
          .catch(logout)
      }
    />
  );
}
